// Ghép NHÂN SỰ PHỤ TRÁCH (tên gõ tay) của TO_PBNS với SỐ HĐ trong NHANSU.

export const MATCH_STATUS = {
    MATCHED: 'KHOP',
    DUPLICATE: 'TRUNG_TEN',
    UNMATCHED: 'KHONG_KHOP',
    LINKED: 'DA_GAN',
};

// Tên do người dùng gõ nên cùng một người có nhiều cách viết hoa/thường và khoảng trắng.
export function normalizePersonName(name) {
    return String(name ?? '').normalize('NFC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('vi');
}

/**
 * Gom các dòng phân bổ theo tên người phụ trách, mỗi tên một nhóm, vì một người thường phụ trách
 * nhiều tổ–công đoạn: chọn SỐ HĐ một lần cho cả nhóm.
 *
 * Dòng đã có SỐ HĐ PHỤ TRÁCH trỏ tới nhân sự còn tồn tại thì xếp vào nhóm đã gắn; SỐ HĐ trỏ tới
 * nhân sự không còn thì coi như chưa gắn để được ghép lại.
 */
export function buildEmployeeMatchGroups(allocations, employees) {
    const employeesByName = new Map();
    const employeeSoHds = new Set();
    for (const employee of employees) {
        const soHd = String(employee['SỐ HĐ'] ?? '').trim();
        // API có lúc trả cùng một dòng hai lần; không bỏ thì một người thành "trùng tên" với chính mình.
        if (!soHd || employeeSoHds.has(soHd)) continue;
        employeeSoHds.add(soHd);
        const key = normalizePersonName(employee['HỌ VÀ TÊN']);
        if (!key) continue;
        employeesByName.set(key, [...(employeesByName.get(key) ?? []), employee]);
    }

    const groups = new Map();
    for (const allocation of allocations) {
        const name = String(allocation['NHÂN SỰ PHỤ TRÁCH'] ?? '').trim();
        if (!name) continue;
        const linkedSoHd = String(allocation['SỐ HĐ PHỤ TRÁCH'] ?? '').trim();
        const isLinked = linkedSoHd && employeeSoHds.has(linkedSoHd);
        const key = `${isLinked ? 'linked' : 'open'}|${normalizePersonName(name)}`;
        if (!groups.has(key)) {
            const candidates = employeesByName.get(normalizePersonName(name)) ?? [];
            let status = MATCH_STATUS.UNMATCHED;
            if (isLinked) status = MATCH_STATUS.LINKED;
            else if (candidates.length === 1) status = MATCH_STATUS.MATCHED;
            else if (candidates.length > 1) status = MATCH_STATUS.DUPLICATE;
            groups.set(key, {
                key,
                name,
                status,
                candidates,
                suggestedSoHd: isLinked ? linkedSoHd : status === MATCH_STATUS.MATCHED ? String(candidates[0]['SỐ HĐ']).trim() : '',
                allocationIds: [],
            });
        }
        groups.get(key).allocationIds.push(allocation['ID TỔ CÔNG ĐOẠN']);
    }

    const order = [MATCH_STATUS.DUPLICATE, MATCH_STATUS.UNMATCHED, MATCH_STATUS.MATCHED, MATCH_STATUS.LINKED];
    return Array.from(groups.values()).sort((left, right) =>
        order.indexOf(left.status) - order.indexOf(right.status) || left.name.localeCompare(right.name, 'vi'));
}

// Các dòng TO_PBNS cần ghi: chỉ nhóm chưa gắn và đã được chọn SỐ HĐ.
export function buildAllocationLinkRows(groups, selectedSoHdByGroup) {
    return groups
        .filter((group) => group.status !== MATCH_STATUS.LINKED)
        .flatMap((group) => {
            const soHd = String(selectedSoHdByGroup[group.key] ?? '').trim();
            if (!soHd) return [];
            return group.allocationIds.map((id) => ({ 'ID TỔ CÔNG ĐOẠN': id, 'SỐ HĐ PHỤ TRÁCH': soHd }));
        });
}
